import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { isError, ServiceQualificationAPI } from '@belong/api';
import { COOKIES, NBN_SESSION_KEYS } from '@belong/constants';
import { setCookie } from '@belong/cookies';
import { INbnPersistedData, IResumptionAnalytics } from '@belong/nbn-utils';
import { clearKeyFromSessionStorage, getValuesFromSessionStorage } from '@belong/session-storage';
import {
  IAddressDetail,
  IAddressWithJwt,
  IServiceQualificationResponse,
  ISqModel,
  SERVICEABILITY_STATUS,
  SQStep
} from '@belong/types';
import { getServiceabilityOutcome } from '../serviceabilityOutcome';
import { setNbnProducts, setServiceQueryDataWithStrategicSQ } from '../sq';
import { STEP_ONE_KEY } from './useStep/types';
import { SetStep, useStep } from './useStep/useStep';
import { loadSqFlowFromSession } from './utils';

export const PARENT_FLOW = { MOVE: 'MOVE' } as const;

export type TParentFlow = keyof typeof PARENT_FLOW;
export interface ISQFlowStrategic {
  // Public (read-only) properties
  step: SQStep;
  setStep: SetStep;
  isProcessing: boolean;
  isComplete: boolean;
  sqLastDoneAt?: number;
  reset: () => void;
  resetSession: () => void;
  selectedAddress?: IAddressDetail;
  serviceabilityDetails?: IServiceQualificationResponse;
  sameAddressError: boolean;
  initialManualInput: boolean;
  setInitialManualInput: Dispatch<SetStateAction<boolean>>;
  showCheckAnotherAddressButton: boolean;
  setShowCheckAnotherAddressButton: Dispatch<SetStateAction<boolean>>;

  // Actions / functions
  selectAddress: (address: IAddressWithJwt, manualResultCount?: number, resumeAnalytics?: IResumptionAnalytics) => void;
  setIsComplete: Dispatch<SetStateAction<boolean>>;
  setIsProcessing: Dispatch<SetStateAction<boolean>>;
}

const makeErrorServiceabilityDetails = (responseType: SERVICEABILITY_STATUS): IServiceQualificationResponse => ({
  serviceQualification: {
    appointmentRequired: false,
    status: responseType
  }
});

type TSqAddressPersistData = Pick<
  INbnPersistedData,
  'addressId' | 'addressJwt' | 'sqLastDoneAt' | 'sqFlow' | 'resumeAnalytics'
>;

export const useStrategicSQFlow = (
  currentAddressId?: string,
  setNbnPersistedData?: (update: Partial<INbnPersistedData>) => void,
  clearNbnPersistedData?: () => void
): ISQFlowStrategic => {
  // initialise state values when there is a current sqFlow in sessionStorage
  // Read once per hook instance (per mount), not every render

  const sqFlow = useMemo(() => loadSqFlowFromSession(), []);

  const [step, setStep] = useStep();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(sqFlow?.isComplete ?? false);
  const [sqLastDoneAt, setSqLastDoneAt] = useState<number | undefined>(sqFlow?.sqLastDoneAt);
  const [selectedAddress, setSelectedAddress] = useState<IAddressDetail | undefined>(sqFlow?.selectedAddress);
  const [sameAddressError, setSameAddressError] = useState<boolean>(false);
  const [structuredAddressResultCount, setStructuredAddressResultCount] = useState<number>(0);
  const [sqAddressPersistData, setSqAddressPersistData] = useState<TSqAddressPersistData | null>(null);
  const [serviceabilityDetails, setServiceabilityDetails] = useState<IServiceQualificationResponse | undefined>(
    sqFlow?.serviceabilityDetails ?? undefined
  );
  const [initialManualInput, setInitialManualInput] = useState<boolean>(sqFlow?.initialManualInput ?? false);
  const [showCheckAnotherAddressButton, setShowCheckAnotherAddressButton] = useState<boolean>(
    sqFlow?.showCheckAnotherAddressButton ?? false
  );

  // Clear all state and return to first step
  const resetSession = useCallback(() => {
    clearKeyFromSessionStorage(NBN_SESSION_KEYS.SQ_FLOW);
    setIsProcessing(false);
    setIsComplete(false);
    setSqLastDoneAt(undefined);
    setSelectedAddress(undefined);
    setServiceabilityDetails(undefined);
    setStructuredAddressResultCount(0);
    setSqAddressPersistData(null);
    setSameAddressError(false);
    setStep(SQStep.ADDRESS_LOOKUP);
    setInitialManualInput(false);
    setShowCheckAnotherAddressButton(false);
  }, []);

  const reset = useCallback(() => {
    resetSession();
    clearNbnPersistedData?.();
  }, []);

  // Check selected address is different to current address
  // Will always be different if currentAddressId is not provided
  const checkForSameAddress = useCallback(
    (addressId?: string): boolean => {
      const isSameAddress = !!addressId && currentAddressId === addressId;

      if (isSameAddress) {
        setStep(SQStep.ADDRESS_LOOKUP);
      }

      setSameAddressError(isSameAddress);
      return isSameAddress;
    },
    [currentAddressId, setStep]
  );

  const handleServiceabilityError = useCallback((): void => {
    setServiceabilityDetails(makeErrorServiceabilityDetails(SERVICEABILITY_STATUS.SOMETHING_WENT_WRONG));
    setInitialManualInput(false);
    setShowCheckAnotherAddressButton(false);
    setStep(SQStep.SERVICEABILITY_DETAILS);
  }, [setStep]);

  const selectAddress = useCallback(
    async (address: IAddressWithJwt, manualResultCount = 0, resumeAnalytics?: IResumptionAnalytics): Promise<any> => {
      setIsProcessing(true);
      setStructuredAddressResultCount(manualResultCount);

      try {
        const sqTimestamp = Date.now();
        const { body: sqResponse } = await ServiceQualificationAPI.getStrategicServiceability(address.addressJwt);

        if (isError(sqResponse)) {
          // When there is an error, we can't set the selected address with the sqResponse
          // use the address returned from search instead, it has same fields as IAddressDetail
          if (address.addressId) {
            setSelectedAddress(address as unknown as IAddressDetail);
          }

          throw new Error(`getStrategicAddressEntity error: ${sqResponse.error?.errorDescription || 'Unknown error'}`);
        }

        // Store address and check for same address
        setSelectedAddress(sqResponse.address);
        if (checkForSameAddress(sqResponse.address?.addressId)) {
          return;
        }

        // Only store serviceability details if no error
        setServiceabilityDetails(sqResponse);
        setInitialManualInput(false);
        setShowCheckAnotherAddressButton(false);
        setStep(SQStep.SERVICEABILITY_DETAILS);
        setSqLastDoneAt(sqTimestamp);

        setSqAddressPersistData({
          addressId: address.addressId,
          addressJwt: address.addressJwt,
          sqLastDoneAt: sqTimestamp,
          resumeAnalytics,
          sqFlow: {
            selectedAddress: sqResponse.address,
            serviceabilityDetails: sqResponse,
            step: SQStep.SERVICEABILITY_DETAILS,
            sqLastDoneAt: sqTimestamp
          }
        });
      } catch {
        setIsComplete(true);
        handleServiceabilityError();
      } finally {
        setIsProcessing(false);
      }
    },
    [checkForSameAddress, handleServiceabilityError, setStep]
  );

  /* ----- [ Actions ] ------------------------------------------------ */

  // Only run this on the first render
  useEffect(() => {
    // Initialise the hook step if there is sqFlow in session.
    // Skip setStep when URL already matches session step to avoid redundant router.push and "Cancel rendering route".
    if (sqFlow?.step) {
      if (step !== sqFlow.step) {
        setStep(sqFlow.step);
      }

      return;
    }

    if (step !== STEP_ONE_KEY) {
      setStep(STEP_ONE_KEY);
    }
  }, []);

  // State persistence - store sqFlow state in sessionStorage
  useEffect(() => {
    if (!sqAddressPersistData) {
      return;
    }

    setNbnPersistedData?.(sqAddressPersistData);
  }, [sqAddressPersistData, setNbnPersistedData]);

  useEffect(() => {
    setCookie(COOKIES.SQ_STEP, step);

    if (!serviceabilityDetails || !selectedAddress || step !== SQStep.SERVICEABILITY_DETAILS) {
      return;
    }

    if (getServiceabilityOutcome(serviceabilityDetails) !== SERVICEABILITY_STATUS.SERVICEABLE) {
      return;
    }

    const sq = getValuesFromSessionStorage<ISqModel>(NBN_SESSION_KEYS.SQ_MODEL);
    // If gnafId is not defined, we haven't written for this address yet
    if (sq?.gnafpId !== undefined) {
      return;
    }

    // Address is serviceable - store relevant details in sessionStorage
    const NbnService = serviceabilityDetails;
    const { serviceQualification } = NbnService;

    const nbnProducts = setNbnProducts(NbnService);

    const sqData = setServiceQueryDataWithStrategicSQ({
      storage: sessionStorage,
      isSqDone: true,
      serviceabilityDetails,
      serviceAddress: selectedAddress,
      addressPID: selectedAddress.gnafId,
      numberOfAddressesFromPID: structuredAddressResultCount,
      masReading: serviceQualification?.mas || {}
    });

    setNbnPersistedData?.({
      ...sqData,
      nbnProducts
    });

    setIsComplete(true);
  }, [serviceabilityDetails, selectedAddress, step, setNbnPersistedData]);

  /* ----- [ Our Interface ] ------------------------------------------------ */

  return {
    initialManualInput,
    showCheckAnotherAddressButton,
    step,
    reset,
    resetSession,
    setStep,
    isComplete,
    sqLastDoneAt,
    isProcessing,
    setInitialManualInput,
    setShowCheckAnotherAddressButton,
    setIsComplete,
    setIsProcessing,
    selectAddress,
    selectedAddress,
    sameAddressError,
    serviceabilityDetails
  };
};
